import React from 'react';
import { Card, CardContent } from './ui/card';
import { Feature } from '../lib/types';

interface HourCalculationProps {
  selectedFeatures: Feature[];
  componentCount: number;
  isMigration?: boolean;
  postTypeCount?: number;
  isContentUpload?: boolean;
  contentUploadLevel?: 'light' | 'medium' | 'heavy';
}

const HourCalculation: React.FC<HourCalculationProps> = ({ 
  selectedFeatures, 
  componentCount,
  isMigration = false,
  postTypeCount = 0,
  isContentUpload = false,
  contentUploadLevel = 'light'
}) => {
  const featuresMinHours = selectedFeatures.reduce((sum, feature) => sum + feature.minHours, 0);
  const featuresMaxHours = selectedFeatures.reduce((sum, feature) => sum + feature.maxHours, 0);
  
  const componentsHours = componentCount * 3;
  const migrationHours = isMigration ? postTypeCount * 2 : 0;
  
  // Calculate content upload hours based on level
  const getContentUploadHours = () => {
    if (!isContentUpload) return 0;
    switch (contentUploadLevel) {
      case 'light': return 4;
      case 'medium': return 8;
      case 'heavy': return 12;
      default: return 0;
    }
  };
  
  const contentUploadHours = getContentUploadHours();
  
  const totalMinHours = featuresMinHours + componentsHours + migrationHours + contentUploadHours;
  const totalMaxHours = featuresMaxHours + componentsHours + migrationHours + contentUploadHours;
  
  // Determine component tier based on count
  const getComponentTier = (count: number): number => {
    if (count < 5) return 1;
    if (count <= 10) return 2;
    if (count <= 15) return 3;
    return 4;
  };
  
  const componentTier = getComponentTier(componentCount);
  
  // Format hours display based on whether min and max are the same
  const formatHours = (min: number, max: number): string => {
    return min === max ? `${min} hrs` : `${min}-${max} hrs`;
  };
  
  // Get content upload level label
  const getContentUploadLevelLabel = () => {
    switch (contentUploadLevel) {
      case 'light': return 'Light';
      case 'medium': return 'Medium';
      case 'heavy': return 'Heavy';
      default: return 'Unknown';
    }
  };
  
  // Separate standard features from custom features
  const standardFeatures = selectedFeatures.filter(feature => 
    !feature.id.startsWith('custom-feature-') && feature.subsection !== 'Custom Features'
  );
  
  const customFeatures = selectedFeatures.filter(feature => 
    feature.id.startsWith('custom-feature-') || feature.subsection === 'Custom Features'
  );
  
  return (
    <div className="space-y-4">
      {selectedFeatures.length === 0 && componentCount === 1 && !isMigration && !isContentUpload ? (
        <p className="text-muted-foreground">Select features to see hour estimates</p>
      ) : (
        <>
          {componentCount > 0 && (
            <div className="flex justify-between py-2 border-b">
              <span>Components ({componentCount}) - Tier {componentTier}</span>
              <span className="font-medium">{componentsHours} hrs</span>
            </div>
          )}
          
          {isContentUpload && (
            <div className="flex justify-between py-2 border-b">
              <span>Content Upload ({getContentUploadLevelLabel()})</span>
              <span className="font-medium">{contentUploadHours} hrs</span>
            </div>
          )}
          
          {isMigration && postTypeCount > 0 && (
            <div className="flex justify-between py-2 border-b">
              <span>Content Migration ({postTypeCount} types)</span>
              <span className="font-medium">{migrationHours} hrs</span>
            </div>
          )}
          
          {/* Standard Features */}
          {standardFeatures.length > 0 && (
            <div className="space-y-2">
              {standardFeatures.map(feature => (
                <div key={feature.id} className="flex justify-between py-2 border-b">
                  <span>{feature.name}</span>
                  <span className="font-medium">
                    {formatHours(feature.minHours, feature.maxHours)}
                  </span>
                </div>
              ))}
            </div>
          )}
          
          {/* Custom Features */}
          {customFeatures.length > 0 && (
            <div className="space-y-2">
              {customFeatures.length > 0 && (
                <h4 className="text-sm font-medium pt-2">Custom Features</h4>
              )}
              {customFeatures.map(feature => (
                <div key={feature.id} className="flex justify-between py-2 border-b">
                  <span>{feature.name}</span>
                  <span className="font-medium">
                    {formatHours(feature.minHours, feature.maxHours)}
                  </span>
                </div>
              ))}
            </div>
          )}
          
          <div className="pt-3 mt-3 border-t border-primary/20">
            <div className="flex justify-between font-bold">
              <span>Total Estimate</span>
              <span>{formatHours(totalMinHours, totalMaxHours)}</span>
            </div>
          </div>
          
          <Card className="bg-secondary mt-6">
            <CardContent className="p-4">
              <h4 className="text-sm font-medium mb-2">Additional Information</h4>
              <p className="text-xs text-muted-foreground">
                This estimate provides a range based on the selected features and components. 
                The actual time may vary depending on specific requirements and complexity.
              </p>
              <div className="mt-2 text-xs">
                <p className="text-muted-foreground">Component Tiers:</p>
                <ul className="list-disc pl-5 text-muted-foreground">
                  <li>Tier 1: Less than 5 components</li>
                  <li>Tier 2: 6-10 components</li>
                  <li>Tier 3: 11-15 components</li>
                  <li>Tier 4: 16+ components</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default HourCalculation; 